import type { ReactNode } from "react";
import { twMerge } from "tailwind-merge";
import { Icon } from "../Icon";
import type { CellContext, Column } from "./DataTable.types";

interface MobileRowProps<T> {
  row: T;
  columns: Column<T>[];
  density: "compact" | "default" | "relaxed";
  isLoading?: boolean;
  onClick?: () => void;
}

const BREAKPOINT_CLASSES = {
  sm: "sm:hidden",
  md: "md:hidden",
  lg: "lg:hidden",
  xl: "xl:hidden",
} as const;

const DESKTOP_BREAKPOINT_CLASSES = {
  sm: "hidden sm:grid",
  md: "hidden md:grid",
  lg: "hidden lg:grid",
  xl: "hidden xl:grid",
} as const;

/**
 * Get the value for a column from a row
 */
function getCellValue<T>(row: T, column: Column<T>): any {
  if (column.accessor) {
    if (typeof column.accessor === "function") {
      return column.accessor(row);
    }
    return row[column.accessor];
  }
  return null;
}

/**
 * Render a cell value for mobile view
 */
function renderMobileCell<T>(value: any, row: T, column: Column<T>, context: CellContext): ReactNode {
  if (column.cell) {
    const CellComponent = column.cell;
    const cellProps = typeof column.cellProps === "function" ? column.cellProps(value, row) : column.cellProps;
    return <CellComponent value={value} row={row} context={context} {...cellProps} />;
  }

  if (column.render) {
    return column.render(value, row, context);
  }

  // Default rendering
  return value?.toString() ?? "—";
}

/**
 * Automatic mobile card renderer
 * Groups columns by priority and formats them appropriately
 */
export function AutoMobileRenderer<T>({ row, columns, density, isLoading, onClick }: MobileRowProps<T>) {
  // Filter and sort columns by mobile config
  const mobileColumns = columns
    .filter((col) => col.mobile !== false)
    .map((col) => {
      const mobileConfig = col.mobile === false ? null : col.mobile;
      return {
        ...col,
        mobilePriority: mobileConfig?.priority ?? 2,
        mobileFormat: mobileConfig?.format ?? "secondary",
        showLabel: mobileConfig?.label ?? mobileConfig?.format === "secondary",
        icon: mobileConfig?.icon,
      };
    })
    .sort((a, b) => a.mobilePriority - b.mobilePriority);

  // Group columns by priority
  const primaryColumns = mobileColumns.filter((col) => col.mobilePriority === 1 || col.mobileFormat === "primary");
  const secondaryColumns = mobileColumns.filter((col) => col.mobilePriority === 2 && col.mobileFormat !== "primary");
  const badgeColumns = mobileColumns.filter((col) => col.mobileFormat === "badge");

  const context: CellContext = {
    isLoading: !!isLoading,
    rowIndex: 0,
    columnIndex: 0,
    density,
  };

  const paddingClasses = {
    compact: "px-3 py-2",
    default: "px-4 py-3",
    relaxed: "px-4 py-4",
  };

  return (
    <div
      className={twMerge(
        "border-b border-border-default bg-background-surface transition-colors",
        onClick && "cursor-pointer hover:bg-background-hover active:bg-background-pressed",
        paddingClasses[density]
      )}
      onClick={onClick}
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? 0 : undefined}
    >
      {/* Primary content - usually the main identifier */}
      {primaryColumns.length > 0 && (
        <div className="mb-2 flex items-start justify-between">
          <div className="flex-1">
            {primaryColumns.map((col) => {
              const value = getCellValue(row, col);
              return (
                <div key={col.id} className="font-medium text-text-primary [&>*]:!justify-start [&>*]:!text-left">
                  {renderMobileCell(value, row, col, context)}
                </div>
              );
            })}
          </div>
          {/* Badges on the right */}
          {badgeColumns.length > 0 && (
            <div className="ml-2 flex flex-wrap gap-1">
              {badgeColumns.map((col) => {
                const value = getCellValue(row, col);
                return (
                  <div key={col.id} className="inline-flex">
                    {renderMobileCell(value, row, col, context)}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Secondary content */}
      {secondaryColumns.length > 0 && (
        <div className="space-y-1">
          {secondaryColumns.map((col) => {
            const value = getCellValue(row, col);
            const showLabel = col.showLabel && col.label;

            return (
              <div key={col.id} className="flex items-center text-sm text-text-secondary">
                {col.icon && <Icon name={col.icon as any} className="mr-1.5 h-4 w-4 flex-shrink-0" />}
                {showLabel && <span className="mr-1 font-medium">{col.label}:</span>}
                <span className="truncate [&>*]:!justify-start [&>*]:!text-left">
                  {renderMobileCell(value, row, col, context)}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/**
 * Simple card renderer - displays all columns in a simple stacked layout
 */
export function CardMobileRenderer<T>({ row, columns, density, isLoading, onClick }: MobileRowProps<T>) {
  const visibleColumns = columns.filter((col) => col.mobile !== false);

  const context: CellContext = {
    isLoading: !!isLoading,
    rowIndex: 0,
    columnIndex: 0,
    density,
  };

  const paddingClasses = {
    compact: "px-3 py-2",
    default: "px-4 py-3",
    relaxed: "px-4 py-4",
  };

  return (
    <div
      className={twMerge(
        "border-b border-border-default bg-background-surface transition-colors",
        onClick && "cursor-pointer hover:bg-background-hover active:bg-background-pressed",
        paddingClasses[density]
      )}
      onClick={onClick}
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? 0 : undefined}
    >
      <div className="space-y-2">
        {visibleColumns.map((col, index) => {
          const value = getCellValue(row, col);
          const isFirst = index === 0;

          return (
            <div key={col.id}>
              {!isFirst && <div className="text-xs text-text-secondary">{col.label}</div>}
              <div
                className={twMerge(
                  isFirst ? "font-medium text-text-primary" : "text-sm text-text-primary",
                  "[&>*]:!justify-start [&>*]:!text-left" // Override any alignment from cells
                )}
              >
                {renderMobileCell(value, row, col, context)}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/**
 * Get the appropriate mobile breakpoint classes
 */
export function getMobileBreakpointClasses(breakpoint: "sm" | "md" | "lg" | "xl" = "md") {
  return {
    mobile: BREAKPOINT_CLASSES[breakpoint],
    desktop: DESKTOP_BREAKPOINT_CLASSES[breakpoint],
  };
}

/**
 * Mobile loading skeleton
 */
export function MobileLoadingSkeleton({ density }: { density: "compact" | "default" | "relaxed" }) {
  const paddingClasses = {
    compact: "px-3 py-2",
    default: "px-4 py-3",
    relaxed: "px-4 py-4",
  };

  return (
    <div className={twMerge("border-b border-border-default bg-background-surface", paddingClasses[density])}>
      <div className="mb-2 h-5 w-3/4 animate-pulse rounded bg-background-muted"></div>
      <div className="space-y-1">
        <div className="h-4 w-1/2 animate-pulse rounded bg-background-muted"></div>
        <div className="h-4 w-2/3 animate-pulse rounded bg-background-muted"></div>
      </div>
    </div>
  );
}
